import { JSX } from "react";

export interface BarDatum {
  label: string;
  value: number;
}

/**
 * Lightweight horizontal bar chart — pure CSS, no dependencies.
 * Bars are scaled to the largest value in the set.
 */
export default function BarChart({
  data,
  color = "#EA33C0",
  emptyText = "No data yet.",
}: {
  data: BarDatum[];
  color?: string;
  emptyText?: string;
}): JSX.Element {
  if (!data || data.length === 0) {
    return <p className="text-sm text-gray-400 py-6 text-center">{emptyText}</p>;
  }

  const max = Math.max(1, ...data.map((d) => d.value));

  return (
    <div className="flex flex-col gap-3">
      {data.map((d) => {
        const pct = (d.value / max) * 100;
        return (
          <div key={d.label} className="flex items-center gap-3">
            <div
              className="w-44 shrink-0 text-sm text-gray-700 truncate text-right"
              title={d.label}
            >
              {d.label}
            </div>
            <div className="flex-1 bg-gray-100 rounded h-7 overflow-hidden">
              <div
                className="h-full rounded flex items-center justify-end px-2 text-xs text-white font-semibold transition-all duration-300"
                style={{
                  width: d.value > 0 ? `${Math.max(pct, 4)}%` : "0%",
                  backgroundColor: color,
                }}
              >
                {d.value > 0 ? d.value : ""}
              </div>
            </div>
            {d.value === 0 && (
              <span className="text-xs text-gray-400 w-5 shrink-0">0</span>
            )}
          </div>
        );
      })}
    </div>
  );
}
